/**
 * FHIR R5 `meta.security` HTEST stamping for the web planner.
 *
 * Mirrors the iOS `MetaSecurity.swift` helpers (OMON-114 Phase 2):
 *
 *   - `HTEST_SECURITY_LABEL` constant — the FHIR R5 v3-ActReason HTEST
 *     coding stamped onto resources created by `dataScope: test` users.
 *   - `metaSecurityFor(claims)` — returns the `security` array for a
 *     write based on the user's active claims.
 *   - `metaForWrite(claims)` — returns the full `meta` map (just the
 *     `security` field for now; future fields like `lastUpdated` can
 *     be added here without changing call sites).
 *
 * Per ADR-0013 §2 + ADR-0016: dataScope + meta.security implement the
 * dev/prod isolation discipline. Stamping at write time is defence in
 * depth for the cross-database write path — Firestore Security Rules
 * (OMON-016) are the load-bearing gate.
 *
 * `dataScope: real` AND `dataScope: both` AND nil claims all return
 * an EMPTY `security: []` array (not absent). Rules can reliably
 * check `meta.security.hasAny([HTEST])` without worrying about
 * missing-field branches. Empty array = explicit "not test" stamp.
 */
import type { AuthClaims } from "./claims";

export const HTEST_SECURITY_LABEL = {
  system: "http://terminology.hl7.org/CodeSystem/v3-ActReason",
  code: "HTEST",
  display: "test health data",
} as const;

/**
 * Build the `meta.security` array for a write based on the user's
 * active claims.
 *
 * Returns `[HTEST_SECURITY_LABEL]` when the user is operating in test
 * scope (`dataScope === "test"`); empty array otherwise. See module
 * docstring for the rationale on `both` and `real`.
 */
export function metaSecurityFor(claims: AuthClaims | null): Array<typeof HTEST_SECURITY_LABEL> {
  if (claims?.dataScope !== "test") return [];
  return [HTEST_SECURITY_LABEL];
}

/**
 * Convenience: build the full `meta` map for a write. Returns an
 * object suitable for `{ ..., meta: metaForWrite(claims), ... }` in
 * a Firestore document body. Empty `security` array is still emitted
 * (per the design note above on `metaSecurityFor`).
 */
export function metaForWrite(claims: AuthClaims | null): { security: Array<typeof HTEST_SECURITY_LABEL> } {
  return { security: metaSecurityFor(claims) };
}
